import React from 'react'

import styles from './ResultDialog.module.css'

interface VPNStateData {
  connection: string
  location?: { code: string; provider: string }
  proxy?: { server?: { host: string; port: number } }
  duration?: number
  lastAction?: string
}

interface ExtensionInfo {
  id: string
  name: string
}

interface BypassItem {
  value: string
  scope: string
}

type ResultType = 'vpnState' | 'extensionList' | 'bypassList'

interface ResultDialogProps {
  type: ResultType
  data: unknown
  onClose: () => void
}

const ResultDialog: React.FC<ResultDialogProps> = ({ type, data, onClose }) => {
  const renderVPNState = (state: VPNStateData) => {
    const duration = state.duration ? Math.floor(state.duration / 1000) : 0
    const mins = Math.floor(duration / 60)
    const secs = duration % 60

    return (
      <>
        <h2 className={styles.title}>VPN STATE</h2>
        <div className={styles.grid}>
          <div className={styles.label}>Status:</div>
          <div className={`${styles.value} ${state.connection === 'CONNECTED' ? styles.connected : styles.disconnected}`}>
            {state.connection}
          </div>

          <div className={styles.label}>Location:</div>
          <div className={styles.value}>
            {state.location?.code || 'None'} {state.location?.provider && `(${state.location.provider})`}
          </div>

          <div className={styles.label}>Proxy:</div>
          <div className={styles.value}>
            {state.proxy?.server?.host
              ? `${state.proxy.server.host}:${state.proxy.server.port}`
              : 'None'}
          </div>

          <div className={styles.label}>Duration:</div>
          <div className={styles.value}>{mins}m {secs}s</div>

          {state.lastAction && (
            <>
              <div className={styles.label}>Last Action:</div>
              <div className={styles.value}>{state.lastAction}</div>
            </>
          )}
        </div>
      </>
    )
  }

  const renderExtensionList = (extensions: ExtensionInfo[]) => {
    return (
      <>
        <h2 className={styles.title}>VULNERABLE EXTENSIONS</h2>
        {extensions.length === 0 ? (
          <p className={styles.emptyMessage}>No other proxy extensions found</p>
        ) : (
          <>
            <p className={styles.subtitle}>
              Found {extensions.length} extension{extensions.length !== 1 ? 's' : ''} that can be disabled:
            </p>
            <ul className={styles.list}>
              {extensions.map((ext) => (
                <li key={ext.id} className={styles.listItem}>
                  <span className={styles.extName}>{ext.name}</span>
                  <span className={styles.extId}>{ext.id}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </>
    )
  }

  const renderBypassList = (items: BypassItem[]) => {
    const userItems = items.filter((i) => i.scope === 'user')

    return (
      <>
        <h2 className={styles.title}>BYPASS LIST</h2>
        <p className={styles.subtitle}>
          Total: {items.length} domains | User-added: {userItems.length}
        </p>
        {userItems.length === 0 ? (
          <p className={styles.emptyMessage}>No user-defined bypass domains</p>
        ) : (
          <ul className={styles.list}>
            {userItems.map((item, idx) => (
              <li key={idx} className={styles.listItem}>
                <span className={styles.domain}>{item.value}</span>
                <span className={styles.warning}>EXPOSED</span>
              </li>
            ))}
          </ul>
        )}
      </>
    )
  }

  const renderContent = () => {
    switch (type) {
      case 'vpnState':
        return renderVPNState(data as VPNStateData)
      case 'extensionList':
        return renderExtensionList(data as ExtensionInfo[])
      case 'bypassList':
        return renderBypassList(data as BypassItem[])
      default:
        return <p>Unknown result type</p>
    }
  }

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeBtn} onClick={onClose}>×</button>
        <div className={styles.content}>
          {renderContent()}
        </div>
      </div>
    </div>
  )
}

export default ResultDialog
